import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../../../axiosInstance';
import { fetchSubFormsDropdownValues } from '../services/api';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import Table from 'apollo-react/components/Table';
import Typography from '../../common/StyledTypography';
import AutocompleteInput from '../../common/Form/autocompleteInput';
import TableActions from '../../common/DataTable/TableActions';

import CommonModal from '../../common/CommonModal';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';

import { Formik, Form, Field } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import Grid from 'apollo-react/components/Grid';
import SwitchInput from '../../common/Form/switchInput';
import TextInput from '../../common/Form/textInput';
import Button from '../../common/Button';
import { updateFormSchema } from '../../../validation/form';
import { useLicensingExperienceOptional } from '../../../contexts/LicensingExperienceContext';

// eslint-disable-next-line no-unused-vars
export default function UpdateForm({ persona }) {
  const { id } = useParams();
  const [subforms, setSubforms] = useState([]);
  const { mode } = useLicensingExperienceOptional();
  const isLicenseTracking = mode === 'license_tracking';
  const isLicensingTool = mode === 'licensing_tool';
  const [forms, setForms] = useState({
    name: '',
    description: '',
    published: false,
    subforms: [],
    has_hcp_licenses_list: false,
    attach_end_user_profile_snapshot: false,
  });
  const [formsCopy, setFormsCopy] = useState({
    forms: {
      name: '',
      description: '',
      published: false,
      subforms: [],
      has_hcp_licenses_list: false,
      attach_end_user_profile_snapshot: false,
    },
    subformsRows: [],
    formPublished: false,
  });
  const [, setFormPublished] = useState(false);
  const [subformsRows, setSubformsRows] = useState([]);
  const [draggedRowIndex, setDraggedRowIndex] = useState();
  const [hoveredRowIndex, setHoveredRowIndex] = useState();
  const [discardModal, setDiscardModal] = useState(false);
  const [t] = useTranslation();

  const isDirty = useMemo(() => {
    const initForms = formsCopy.forms;
    const initRows = formsCopy.subformsRows;
    return (
      (forms.name || '') !== (initForms.name || '') ||
      (forms.description || '') !== (initForms.description || '') ||
      forms.published !== initForms.published ||
      JSON.stringify((subformsRows || []).map((s) => s.id)) !==
        JSON.stringify((initRows || []).map((s) => s.id))
    );
  }, [forms, subformsRows, formsCopy]);

  const { navigateBack } = useUnsavedChangesWarning(isDirty);

  const getForm = async () => {
    await axiosInstance
      // eslint-disable-next-line no-undef
      .get(`${process.env.REACT_APP_SERVER_URL}/api/forms/${id}`, {
        withCredentials: true,
      })
      .then((response) => {
        console.log(response.data);
        setForms(response.data);
        setFormPublished(response.data.published);
        setSubformsRows(response.data.subforms);
        setFormsCopy({
          forms: response.data,
          formPublished: response.data.published,
          subformsRows: response.data.subforms,
        });
      })
      .catch((err) => console.log(err));
  };

  const getSubforms = async () => {
    try {
      const { data } = await fetchSubFormsDropdownValues();
      setSubforms(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getForm();
    getSubforms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormChange = (name, value) => {
    setForms({
      ...forms,
      [name]: value,
    });
  };

  const handleSubformsChange = (e) => {
    handleFormChange('subforms', e);
    setSubformsRows(e);
  };

  const handlePublishedChange = (checked) => {
    handleFormChange('published', checked);
  };

  const handleFormSubmit = async () => {
    const updatedForms = { ...forms };
    updatedForms.subforms = subformsRows.map(({ id }, index) => ({
      id,
      order: index,
    }));

    try {
      await axiosInstance.put(`/api/forms/${id}`, updatedForms, {
        withCredentials: true,
      });
      toast.success(t('Form Updated Successfully!'), {
        position: 'top-center',
      });
      navigateBack();
    } catch (error) {
      console.error(t('Error saving form'), ':', error);
      const details = error.response?.data?.error?.details;
      if (details) {
        const messages = Array.isArray(details)
          ? details.map((d) => d.message || 'Validation error')
          : Object.values(details);
        toast.error(messages.join('\n'), { position: 'top-center' });
      } else {
        const message =
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          t('Failed to save form.');
        toast.error(message, { position: 'top-center' });
      }
      return false;
    }
  };

  const tableRowProps = {
    draggable: true,
    style: { cursor: 'grab' },
    onDragStart: (e) => {
      const row = e.target.closest('tr');
      const children = Array.from(e.target.closest('tbody').children);
      setDraggedRowIndex(children.indexOf(row));
      // console.log('draggedRowIndex', children.indexOf(row));
    },
    onDragOver: (e) => {
      e.preventDefault();
      const children = Array.from(e.target.closest('tbody').children);
      const hoveredRow = e.target.closest('tr');
      setHoveredRowIndex(children.indexOf(hoveredRow));
    },
    onDrop: () => {
      // console.log('hoveredRowIndex', hoveredRowIndex);
      const rowsCopy = [...subformsRows];
      const movedRow = subformsRows[draggedRowIndex];
      rowsCopy.splice(draggedRowIndex, 1);
      rowsCopy.splice(hoveredRowIndex, 0, movedRow);
      setSubformsRows(rowsCopy);
      setForms({ ...forms, subforms: rowsCopy });
      setHoveredRowIndex(null);
      setDraggedRowIndex(null);
    },
  };

  const ActionCell = ({ row }) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <TableActions
          actions={[
            {
              text: 'Delete',
              color: 'secondary',
              style: {
                border: 'solid 1px #D9D9D9',
              },
              onClick: (row) => {
                const updatedSubForms = subformsRows.filter(
                  (subform) => subform.id !== row.id
                );
                setSubformsRows(updatedSubForms);
                setForms({
                  ...forms,
                  subforms: updatedSubForms,
                });
              },
            },
          ]}
          row={row}
        />
      </div>
    );
  };

  return (
    <div>
      <div
        className="content"
        style={{
          border: 'solid 1px #E9E9E9',
          padding: '24px',
          borderRadius: '5px',
        }}
      >
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={12}>
            <Formik
              initialValues={forms}
              enableReinitialize={true}
              onSubmit={handleFormSubmit}
              validationSchema={toFormikValidationSchema(updateFormSchema(t))}
            >
              <Form>
                <Field name="name">
                  {({ field, meta }) => (
                    <TextInput
                      {...field}
                      meta={meta}
                      label={'Form name'}
                      value={forms?.[`name`] || ''}
                      placeholder={t('Form Name')}
                      onChange={(value) => handleFormChange('name', value)}
                      required
                    />
                  )}
                </Field>

                <Field name="description">
                  {({ field, meta }) => (
                    <TextInput
                      {...field}
                      meta={meta}
                      label={'Form Description'}
                      value={forms?.[`description`]}
                      onChange={(value) =>
                        handleFormChange('description', value)
                      }
                      multiline={true}
                      textareaWidth={'100%'}
                    />
                  )}
                </Field>

                <Field name="subforms">
                  {({ field, meta }) => (
                    <AutocompleteInput
                      {...field}
                      meta={meta}
                      label={'Sub forms'}
                      options={subforms}
                      onChange={handleSubformsChange}
                      multiple={true}
                      value={forms?.subforms}
                    />
                  )}
                </Field>
                <Typography
                  variant="bodySmall"
                  emphasis="low"
                  gutterBottom
                  style={{
                    marginTop: '35px',
                    textDecoration: 'underline',
                  }}
                >
                  {t('subforms_arrange')}
                </Typography>
                <Table
                  columns={[
                    {
                      header: 'Sub forms',
                      accessor: 'label',
                    },
                    {
                      accessor: 'action',
                      customCell: ActionCell,
                      align: 'right',
                    },
                  ]}
                  rows={subformsRows}
                  hidePagination
                  rowId="id"
                  rowProps={tableRowProps}
                />

                <Field name="published">
                  {({ field, meta }) => (
                    <SwitchInput
                      {...field}
                      meta={meta}
                      label={t('Publish')}
                      checked={forms.published === 1}
                      value={forms?.published}
                      onChange={handlePublishedChange}
                      style={{ marginTop: '16px' }}
                    />
                  )}
                </Field>

                {isLicenseTracking ? (
                  <SwitchInput
                    label="License Information"
                    helperText="Adds the License Information section to this form when enabled."
                    checked={!!forms.has_hcp_licenses_list}
                    onChange={(checked) =>
                      handleFormChange('has_hcp_licenses_list', checked)
                    }
                    style={{ marginTop: '8px', marginBottom: '4px' }}
                  />
                ) : null}
                {isLicensingTool ? (
                  <SwitchInput
                    label="Attach External-user profile on new requests"
                    helperText="When enabled, opening a new request from this form snapshots the applicant’s saved HCP profile on the request (reviewer copy)."
                    checked={!!forms.attach_end_user_profile_snapshot}
                    onChange={(checked) =>
                      handleFormChange(
                        'attach_end_user_profile_snapshot',
                        checked
                      )
                    }
                    style={{ marginTop: '8px', marginBottom: '4px' }}
                  />
                ) : null}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '50px',
                  }}
                >
                  <Button
                    color="primary"
                    onClick={() => setDiscardModal(true)}
                    style={{
                      padding: '12px 22px',
                      width: '157px',
                      height: '40px',
                      marginRight: '24px',
                    }}
                    text={t('Discard')}
                  />

                  <Button
                    color="primary"
                    type="submit"
                    style={{
                      padding: '12px 22px',
                      width: '157px',
                      height: '40px',
                    }}
                    text={t('Save')}
                  />
                </div>

                {/* {error && <p>{error}</p>} */}
              </Form>
            </Formik>
          </Grid>
        </Grid>
        <CommonModal
          open={discardModal}
          message="Are you sure you want to discard your changes?"
          buttonProps={[
            {
              label: 'Cancel',
              onClick: () => {
                setDiscardModal(false);
              },
            },
            {
              label: 'Confirm',
              onClick: () => {
                setDiscardModal(false);
                setForms(formsCopy.forms);
                setSubformsRows(formsCopy.subformsRows);
                setFormPublished(formsCopy.formPublished);
              },
            },
          ]}
          hideHeader
          id="hideHeader"
        />
      </div>
    </div>
  );
}
